import { memo, useEffect, useState } from 'react'
import type { NodeProps } from '@xyflow/react'
import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react'
import { estimateVideo, type VideoModel, type VideoPart } from '../../shared/services/videoService'
import { SkillGlyph } from '@shared/components/SkillGlyph'

/**
 * The Video card (Huanrui, 2026-10-08)
 * -------------------------------------------------------------
 * Two ports on the left: 1 is the first frame (needed), 2 the last frame (if
 * there is one) -- the wires say which, so the card does not repeat the
 * pictures (Huanrui, 2026-10-08). The video comes out on the right, in a frame that shows the
 * WonderWorks loader while Kling makes it -- minutes, not seconds -- and then
 * plays. The settings are the Video page's: model, length, sound, and what
 * happens.
 *
 * FRAMES BETWEEN (Huanrui, 2026-10-27), on this card only: with Kling v4,
 * ports for up to 8 frames between the first (S) and the last (E) appear, one
 * more than are wired, numbered in the order the video passes through them.
 * And the video can be told what happens part by part -- between each two
 * frames -- each part with its own seconds; the length is then their sum.
 */

export interface VideoNodeData extends Record<string, unknown> {
  skillRef: string
  name: string
  glyph: string
  glow: string
  open?: boolean
  model?: string
  seconds?: number
  audio?: boolean
  prompt?: string
  /** Told part by part (Kling v4 with frames between). On unless unticked (Huanrui, 2026-10-27). */
  perPart?: boolean
  /** Each part's prompt and seconds, kept while the frames change. */
  parts?: VideoPart[]
  /** Runs in flight from this card, by id. */
  runs?: string[]
  error?: string
  /** When a press last ended without starting a run: clears "Starting…". */
  tried?: number
  /** What the last video cost, in credits (1000 = $1). */
  credits?: number
  madeCount?: number

  /* handed in by the board, never stored */
  models?: VideoModel[] | null
  live?: number
  maxLive?: number
  /** The pictures on each port, or null: whether there is a first frame. */
  first?: string | null
  last?: string | null
  /** Which of the ports between have a picture, by number (1-8), in order. */
  middles?: number[]
  onEdit?: (patch: Partial<VideoNodeData>) => void
  onRun?: () => void
}

/** The ports, by name: what each edge into this card says it is. */
export const FIRST_PORT = 'first'
export const LAST_PORT = 'last'
/** The ports between, `mid-1` to `mid-8` (Kling v4). */
export const MID_PORT = (n: number) => `mid-${n}`
export const MAX_MIDDLE_PORTS = 8

/** The parts a video told part by part has: one more than the frames between, each with its words and seconds. */
export function partsFor(parts: VideoPart[] | undefined, count: number): VideoPart[] {
  return Array.from({ length: count }, (_, i) => parts?.[i] ?? { prompt: '', seconds: 3 })
}

export const VideoNode = memo(function VideoNode({ id, data, selected }: NodeProps) {
  const card = data as VideoNodeData
  const isOpen = card.open !== false
  const models = card.models ?? null
  const mine = (card.runs ?? []).length
  const live = card.live ?? mine
  const full = live >= (card.maxLive ?? 5)
  const edit = (patch: Partial<VideoNodeData>) => card.onEdit?.(patch)

  const [starting, setStarting] = useState(false)
  useEffect(() => {
    setStarting(false)
  }, [mine, card.error, card.tried])
  useEffect(() => {
    if (!starting) return
    const stop = setTimeout(() => setStarting(false), 15000)
    return () => clearTimeout(stop)
  }, [starting])

  const model = models?.find((one) => one.key === card.model) ?? models?.[0]
  const seconds = model
    ? Math.min(model.maxSeconds, Math.max(model.minSeconds, card.seconds ?? model.defaultSeconds))
    : card.seconds ?? 5
  // Always with sound (Huanrui, 2026-10-27): no longer a choice.
  const audio = true
  const hasFirst = Boolean(card.first)
  const prompt = card.prompt ?? ''

  // Frames between: only for a model that takes them; one empty port more than the highest in use.
  const maxMiddle = Math.min(MAX_MIDDLE_PORTS, model?.maxMiddle ?? 0)
  const middles = maxMiddle > 0 ? (card.middles ?? []).filter((n) => n <= maxMiddle) : []
  const midPorts = maxMiddle > 0 ? Math.min(maxMiddle, Math.max(0, ...middles) + 1) : 0
  const remeasure = useUpdateNodeInternals()
  useEffect(() => {
    remeasure(id)
  }, [id, midPorts, remeasure])
  const perPart = card.perPart !== false && middles.length > 0
  const parts = perPart ? partsFor(card.parts, middles.length + 1) : []
  const partSeconds = parts.reduce((sum, one) => sum + (Number(one.seconds) || 0), 0)
  const length = perPart ? partSeconds : seconds
  const credits = model ? Math.round(estimateVideo(model, length, audio) * 1000) : 0
  const partsReady =
    !perPart ||
    (parts.every((one) => one.prompt.trim() && one.seconds >= 1) &&
      model !== undefined &&
      partSeconds >= model.minSeconds &&
      partSeconds <= model.maxSeconds)
  const said = perPart ? parts.every((one) => one.prompt.trim()) : Boolean(prompt.trim())
  const setPart = (index: number, change: Partial<VideoPart>) => {
    const next = partsFor(card.parts, Math.max(middles.length + 1, card.parts?.length ?? 0))
    next[index] = { ...next[index]!, ...change }
    edit({ parts: next })
  }
  /** What a part runs between: S, the frames between by their order, E (or on, with no last frame). */
  const partName = (index: number) => {
    const from = index === 0 ? 'S' : String(index)
    const to = index < middles.length ? String(index + 1) : card.last ? 'E' : 'on'
    return `${from} → ${to}`
  }

  const port = (portId: string, label: string, mark: string, top: number) => (
    <Handle
      key={portId}
      type="target"
      id={portId}
      position={Position.Left}
      className="cnode__port pnode__port"
      style={{ top: `${top}%` }}
      title={label}
    >
      <span className="pnode__portnum">{mark}</span>
    </Handle>
  )
  // Spread down the side: S, the ports between, E.
  const slots = 2 + midPorts
  const at = (index: number) => ((index + 1) / (slots + 1)) * 100

  return (
    <div
      className={[
        'cnode',
        'pnode',
        'vnode',
        isOpen ? 'pnode--open' : '',
        selected ? 'cnode--selected' : '',
        hasFirst ? 'cnode--lit' : '',
        mine > 0 ? 'cnode--running' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--tile-glow': card.glow } as React.CSSProperties}
    >
      {port(FIRST_PORT, 'First frame', maxMiddle > 0 ? 'S' : '1', at(0))}
      {Array.from({ length: midPorts }, (_, i) =>
        port(MID_PORT(i + 1), `Frame between, ${i + 1} (optional)`, String(i + 1), at(i + 1)),
      )}
      {port(LAST_PORT, 'Last frame (optional)', maxMiddle > 0 ? 'E' : '2', at(slots - 1))}
      <Handle type="source" id="out" position={Position.Right} className="cnode__port" />

      <div className="pnode__head">
        <span className="cnode__glyph" aria-hidden="true">
          <SkillGlyph glyph={card.glyph} />
        </span>
        <span className="cnode__name">{card.name}</span>
        <button
          type="button"
          className="pnode__toggle nodrag"
          onClick={() => edit({ open: !isOpen })}
          title={isOpen ? 'Fold this card' : 'Unfold this card'}
          aria-label={isOpen ? 'Fold this card' : 'Unfold this card'}
        >
          {isOpen ? '−' : '+'}
        </button>
      </div>

      {isOpen ? (
        <div className="pnode__body">
          {middles.length > 0 ? (
            <label className="vnode__perpart nodrag">
              <input type="checkbox" checked={perPart} onChange={(event) => edit({ perPart: event.target.checked })} />
              A prompt for each part, between each two frames
            </label>
          ) : null}
          {perPart ? (
            <div className="vnode__parts">
              {parts.map((one, index) => (
                <div key={index} className="vnode__part">
                  <div className="vnode__part-head">
                    <span className="pnode__label">Part {index + 1} · {partName(index)}</span>
                    <input
                      className="vnode__part-seconds nodrag"
                      type="number"
                      min={1}
                      max={model?.maxSeconds ?? 30}
                      value={one.seconds}
                      onChange={(event) => setPart(index, { seconds: Math.max(1, Math.round(Number(event.target.value) || 1)) })}
                    />
                    <span className="vnode__part-unit">s</span>
                  </div>
                  <textarea
                    className="pnode__prompt nodrag nowheel"
                    rows={2}
                    value={one.prompt}
                    placeholder="What happens in this part"
                    onChange={(event) => setPart(index, { prompt: event.target.value })}
                  />
                </div>
              ))}
            </div>
          ) : (
            <textarea
              className="pnode__prompt nodrag nowheel"
              value={prompt}
              rows={3}
              onChange={(event) => edit({ prompt: event.target.value })}
            />
          )}

          {models ? (
            <>
              <div className="pnode__field">
                <span className="pnode__label">Model</span>
                <div className="pnode__chips">
                  {models.map((one) => (
                    <button
                      key={one.key}
                      type="button"
                      className={`pnode__chip nodrag${one.key === model?.key ? ' pnode__chip--on' : ''}`}
                      disabled={!one.ready}
                      title={one.ready ? one.blurb : 'Not switched on yet'}
                      onClick={() => edit({ model: one.key })}
                    >
                      {one.label}
                    </button>
                  ))}
                </div>
              </div>

              {model && perPart ? (
                <p className={partsReady || !parts.every((one) => one.prompt.trim()) ? 'xnode__hint' : 'pnode__error'}>
                  {partSeconds} s in all · {model.label} makes {model.minSeconds}–{model.maxSeconds} s
                </p>
              ) : null}
              {model && !perPart ? (
                <div className="pnode__field">
                  <span className="pnode__label">
                    Length · {seconds} s
                  </span>
                  <input
                    type="range"
                    className="vnode__range nodrag nowheel"
                    min={model.minSeconds}
                    max={model.maxSeconds}
                    step={1}
                    value={seconds}
                    onChange={(event) => edit({ seconds: Number(event.target.value) })}
                  />
                </div>
              ) : null}

            </>
          ) : (
            <p className="pnode__error">Video could not be loaded.</p>
          )}
          {models && model && !model.ready ? <p className="pnode__error">Video is not switched on yet.</p> : null}
          {card.error ? <p className="pnode__error">{card.error}</p> : null}
        </div>
      ) : null}

      {isOpen || mine > 0 ? (
        <button
          type="button"
          className="cnode__run pnode__run nodrag"
          onClick={() => {
            setStarting(true)
            card.onRun?.()
          }}
          disabled={starting || full || !hasFirst || !said || !partsReady || !model?.ready}
          title={
            full
              ? `${live} already going, here or elsewhere — wait for one to finish`
              : !hasFirst
                ? 'Wire a first frame into port 1'
                : !said
                  ? perPart
                    ? 'Say what happens in each part'
                    : 'Say what should happen'
                  : !partsReady
                    ? `The parts add up to ${partSeconds} s`
                  : 'Make a video from these frames'
          }
        >
          {starting ? (
            'Starting…'
          ) : full ? (
            'Queued — ' + live + ' already going'
          ) : (
            <>
              Run
              {credits > 0 ? <span className="pnode__credits">{credits} credits</span> : null}
              {mine > 0 ? <span className="pnode__credits">· {mine} going</span> : null}
            </>
          )}
        </button>
      ) : null}

      {mine === 0 && card.credits ? (
        <span className="pnode__spent">
          {card.madeCount ?? 0} made · {card.credits} credits
        </span>
      ) : null}

      {!isOpen && card.error ? <span className="cnode__error">{card.error}</span> : null}
    </div>
  )
})
